import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminError, AdminResult } from './adminApi';

/** After a 429 the poller waits this long between requests until one succeeds (contract admin-ui). */
export const SLOW_INTERVAL_MS = 60000;

export type Fetcher<T> = (signal: AbortSignal) => Promise<AdminResult<T>>;

export interface PollingState<T> {
  data: T | null;
  error: AdminError | null;
  /** Epoch ms of the last successful response. */
  updatedAt: number | null;
  /** The last request failed but earlier data is still shown. */
  stale: boolean;
  refresh: () => void;
}

interface Options {
  onError?: (error: AdminError) => void;
}

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * Fetch now, then again `intervalMs` after each response (chained timers, so a
 * slow server can never cause overlapping requests). Pauses while the tab is
 * hidden and fetches immediately on return. `intervalMs = null` fetches once
 * (and on `refresh`) — used by panels that load on demand. A new `fetcher`
 * identity starts over, so callers memoise it on its inputs.
 */
export function usePolling<T>(fetcher: Fetcher<T>, intervalMs: number | null, options: Options = {}): PollingState<T> {
  const [state, setState] = useState<{ data: T | null; error: AdminError | null; updatedAt: number | null }>(
    { data: null, error: null, updatedAt: null },
  );
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;
  const runRef = useRef<() => void>(() => {});

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight: AbortController | null = null;
    let slow = false;
    let halted = false;
    const polling = intervalMs !== null;

    setState(previous => (previous.data === null && previous.error === null && previous.updatedAt === null
      ? previous : { data: null, error: null, updatedAt: null }));

    const clear = () => { if (timer) clearTimeout(timer); timer = null; };
    const schedule = () => {
      clear();
      if (!active || !polling || halted || isHidden()) return;
      timer = setTimeout(run, slow ? SLOW_INTERVAL_MS : (intervalMs as number));
    };
    function run() {
      if (!active || inFlight || halted) return;
      clear();
      const controller = new AbortController();
      inFlight = controller;
      fetcher(controller.signal)
        .catch((): AdminResult<T> => ({ ok: false, error: { kind: 'unavailable' } }))
        .then(result => {
          if (!active || controller.signal.aborted) return;
          inFlight = null;
          if (result.ok) {
            slow = false;
            setState({ data: result.data, error: null, updatedAt: Date.now() });
          } else {
            const { error } = result;
            if (error.kind === 'slow') slow = true;
            if (error.kind === 'signin' || error.kind === 'forbidden') halted = true;
            setState(previous => ({ ...previous, error }));
            onErrorRef.current?.(error);
          }
          schedule();
        });
    }

    const onVisibility = () => {
      if (!polling) return;
      if (isHidden()) clear();
      else run();
    };

    runRef.current = run;
    document.addEventListener('visibilitychange', onVisibility);
    if (!polling || !isHidden()) run();

    return () => {
      active = false;
      clear();
      inFlight?.abort();
      inFlight = null;
      runRef.current = () => {};
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetcher, intervalMs]);

  const refresh = useCallback(() => runRef.current(), []);

  return { ...state, stale: state.error !== null && state.data !== null, refresh };
}
